import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import Select, { type StylesConfig } from "react-select";
import { getCustomSelectStyles } from "../../../../../admin/shared";

type AdapterOption = { value: string; label: string };
import type { VehicleSocket } from "../../../../../../store/admin/vehiclesSocketStore";
import { useModalEvidenceSubscription } from "../../../../../../hooks/useModalEvidenceSubscription";
import { useEvidenceModalSocketStore } from "../../../../../../store/admin/evidenceModalSocketStore";
import type { EvidenceItem } from "../../../../../../store/admin/evidenceSocketStore";
import type { EvidenceFilter } from "../../../../../../evidenceSocket";
import { useCompanyIntegratorsStore } from "../../../../../../store/admin/companyIntegratorsStore";
import {
  formatPayload,
  formatTime,
  payloadFileMeta,
  downloadText,
  screenshotPayload,
  buildFileName,
} from "../../../../../integradores/helpers";
import { useEscapeKey } from "../../../../../../hooks/useEscapeKey";

interface VehicleEvidenceModalProps {
  vehicle: VehicleSocket;
  isDarkMode: boolean;
  onClose: () => void;
}

export default function VehicleEvidenceModal({
  vehicle,
  isDarkMode,
  onClose,
}: VehicleEvidenceModalProps) {
  useEscapeKey(onClose);
  const { t } = useTranslation("common");

  const [selectedAdapter, setSelectedAdapter] = useState<string | null>(null);
  const [selectedRetransmission, setSelectedRetransmission] = useState<
    string | null
  >(null);
  const [statusFilter, setStatusFilter] = useState<"all" | "ok" | "error">(
    "all",
  );
  const [selected, setSelected] = useState<EvidenceItem | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  const [busyShot, setBusyShot] = useState<string | null>(null);

  const integrators = useCompanyIntegratorsStore((state) => state.integrators);
  const retransmissions = useCompanyIntegratorsStore(
    (state) => state.retransmissions,
  );

  const selectStyles = useMemo(
    () =>
      getCustomSelectStyles(isDarkMode) as StylesConfig<AdapterOption, false>,
    [isDarkMode],
  );

  const filter = useMemo<EvidenceFilter>(
    () => ({ device_ids: [vehicle.id] }),
    [vehicle.id],
  );

  const { isConnected } = useModalEvidenceSubscription(filter);
  const connecting = useEvidenceModalSocketStore((state) => state.connecting);
  const allEvidences = useEvidenceModalSocketStore((state) => state.evidences);
  const clearEvidences = useEvidenceModalSocketStore(
    (state) => state.clearEvidences,
  );

  useEffect(() => {
    clearEvidences();
  }, [clearEvidences]);

  const deviceEvidences = useMemo(
    () =>
      allEvidences.filter(
        (ev) => !ev.device_id || ev.device_id === vehicle.id,
      ),
    [allEvidences, vehicle.id],
  );

  const hasReceivedRef = useRef(false);
  if (deviceEvidences.length > 0) hasReceivedRef.current = true;

  const retransmissionNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const r of retransmissions) {
      map.set(r.id, r.customer_name || r.label || r.id);
    }
    return map;
  }, [retransmissions]);

  const adapterOptions = useMemo(() => {
    // Solo integradores cuyas retransmisiones cubren a este device
    // (scope "all" o device_ids incluye al device).
    const coveredAdapters = new Set<string>();
    for (const r of retransmissions) {
      if (!r.adapter) continue;
      if (r.coversAllDevices || r.device_ids.includes(vehicle.id)) {
        coveredAdapters.add(r.adapter);
      }
    }

    const labels = new Map<string, string>();
    for (const it of integrators) {
      if (it.adapter && coveredAdapters.has(it.adapter)) {
        labels.set(it.adapter, it.name || it.adapter);
      }
    }
    // Fallback: cualquier integrador que ya emitió evidencia para este device
    // (cubre scopes por grupo que no podemos resolver aquí).
    for (const ev of deviceEvidences) {
      if (ev.integrator_adapter && !labels.has(ev.integrator_adapter)) {
        labels.set(ev.integrator_adapter, ev.integrator_adapter);
      }
    }
    return Array.from(labels, ([value, label]) => ({ value, label }));
  }, [integrators, retransmissions, deviceEvidences, vehicle.id]);

  const hasIntegrators = adapterOptions.length > 0;

  const retransmissionOptions = useMemo<AdapterOption[]>(
    () =>
      retransmissions
        .filter(
          (r) => r.coversAllDevices || r.device_ids.includes(vehicle.id),
        )
        .map((r) => ({
          value: r.id,
          label: r.customer_name ? `${r.label} · ${r.customer_name}` : r.label,
        })),
    [retransmissions, vehicle.id],
  );

  const hasRetransmissions = retransmissionOptions.length > 0;

  const autoSelectedRef = useRef(false);
  useEffect(() => {
    if (
      !autoSelectedRef.current &&
      selectedAdapter === null &&
      adapterOptions.length === 1
    ) {
      autoSelectedRef.current = true;
      setSelectedAdapter(adapterOptions[0].value);
    }
  }, [adapterOptions, selectedAdapter]);

  const evidences = useMemo(
    () =>
      deviceEvidences.filter((ev) => {
        if (selectedAdapter && ev.integrator_adapter !== selectedAdapter)
          return false;
        if (
          selectedRetransmission &&
          ev.retransmission_id !== selectedRetransmission
        )
          return false;
        if (statusFilter === "ok" && !ev.integrator_ok) return false;
        if (statusFilter === "error" && ev.integrator_ok) return false;
        return true;
      }),
    [deviceEvidences, selectedAdapter, selectedRetransmission, statusFilter],
  );

  const copyText = async (text: string, which: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(which);
      setTimeout(() => setCopied(null), 1500);
    } catch {
      setCopied(null);
    }
  };

  const handleDownloadText = (field: "request" | "response") => {
    if (!selected) return;
    const { ext, mime } = payloadFileMeta(selected[field]);
    downloadText(
      formatPayload(selected[field]),
      buildFileName(selected, field, ext),
      mime,
    );
  };

  const handleScreenshot = async (field: "request" | "response") => {
    if (!selected) return;
    const key = `${field}-png`;
    setBusyShot(key);
    try {
      await screenshotPayload(
        `${selected.imei || selected.device_id} - ${field.toUpperCase()}`,
        formatTime(selected.sent_at),
        formatPayload(selected[field]),
        buildFileName(selected, field, "png"),
      );
    } catch {
      /* noop */
    } finally {
      setBusyShot(null);
    }
  };

  const bgColor = isDarkMode ? "bg-dark-bg" : "bg-white";
  const textColor = isDarkMode ? "text-gray-300" : "text-gray-700";
  const textSecondary = isDarkMode ? "text-gray-400" : "text-gray-500";
  const borderColor = isDarkMode ? "border-gray-700" : "border-gray-200";
  const surfaceBg = isDarkMode ? "bg-dark-surface" : "bg-gray-50";
  const btnGhost = isDarkMode
    ? "bg-transparent border border-dark-border rounded text-[11px] px-2 py-1 text-gray-300 cursor-pointer hover:bg-dark-card transition-colors flex items-center gap-1"
    : "bg-transparent border border-gray-300 rounded text-[11px] px-2 py-1 text-gray-600 cursor-pointer hover:bg-gray-100 transition-colors flex items-center gap-1";

  const vehicleName = vehicle.vehicle?.name || vehicle.imei || vehicle.id;

  return createPortal(
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div
        className={`${bgColor} rounded-xl max-w-5xl w-full min-h-[70vh] max-h-[88vh] overflow-hidden border ${borderColor} shadow-2xl flex flex-col`}
      >
        {/* Header */}
        <div
          className={`flex items-center justify-between p-4 border-b ${borderColor}`}
        >
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-8 h-8 rounded-lg bg-cyan-600 flex items-center justify-center shrink-0">
              <i className="ri-file-code-line text-white text-lg"></i>
            </div>
            <div className="min-w-0">
              <h2 className={`font-semibold text-lg ${textColor} truncate`}>
                {t("integradores.evidenceTitle", "Evidencias de integradores")}
              </h2>
              <p className={`text-xs ${textSecondary} truncate`}>{vehicleName}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span
              className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium ${
                connecting && !isConnected
                  ? "bg-amber-500/15 text-amber-400"
                  : isConnected
                    ? "bg-emerald-500/15 text-emerald-400"
                    : "bg-red-500/15 text-red-400"
              }`}
            >
              <i
                className={`${connecting && !isConnected ? "ri-loader-4-line animate-spin" : "ri-plug-line"} text-[10px]`}
              ></i>
              {connecting && !isConnected
                ? t("integradores.connecting", "Conectando")
                : isConnected
                  ? t("integradores.connected", "Conectado")
                  : t("integradores.disconnected", "Desconectado")}
            </span>
            <button
              onClick={onClose}
              className={`p-2 ${isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-100"} rounded-lg transition-colors`}
            >
              <i className={`ri-close-line text-xl ${textColor}`}></i>
            </button>
          </div>
        </div>

        {/* Toolbar: integrator + status */}
        <div
          className={`px-4 py-2.5 flex items-center gap-3 flex-wrap border-b ${borderColor} ${surfaceBg}`}
        >
          {hasIntegrators && (
            <div className="flex items-center gap-2 min-w-0">
              <span
                className={`text-[11px] font-semibold uppercase tracking-wide ${textSecondary} shrink-0`}
              >
                {t("integradores.chooseIntegrator", "Integrador")}
              </span>
              <div className="w-64 max-w-full">
                <Select<AdapterOption>
                  value={
                    adapterOptions.find((o) => o.value === selectedAdapter) ??
                    null
                  }
                  onChange={(opt) => {
                    setSelectedAdapter(opt?.value ?? null);
                    setSelected(null);
                  }}
                  options={adapterOptions}
                  placeholder={t(
                    "integradores.selectIntegratorPlaceholder",
                    "Seleccionar integrador",
                  )}
                  styles={selectStyles}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  isClearable
                />
              </div>
            </div>
          )}

          {hasRetransmissions && (
            <div className="flex items-center gap-2 min-w-0">
              <span
                className={`text-[11px] font-semibold uppercase tracking-wide ${textSecondary} shrink-0`}
              >
                {t("integradores.chooseRetransmission", "Retransmisión")}
              </span>
              <div className="w-64 max-w-full">
                <Select<AdapterOption>
                  value={
                    retransmissionOptions.find(
                      (o) => o.value === selectedRetransmission,
                    ) ?? null
                  }
                  onChange={(opt) => {
                    setSelectedRetransmission(opt?.value ?? null);
                    setSelected(null);
                  }}
                  options={retransmissionOptions}
                  placeholder={t(
                    "integradores.select.retransmission",
                    "Selecciona retransmisión...",
                  )}
                  styles={selectStyles}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  isClearable
                />
              </div>
            </div>
          )}

          <div
            className={`flex items-center rounded-lg p-0.5 ml-auto ${isDarkMode ? "bg-dark-card" : "bg-gray-100"}`}
          >
            {(["all", "ok", "error"] as const).map((s) => (
              <button
                key={s}
                onClick={() => setStatusFilter(s)}
                className={`text-xs px-3 py-1 rounded-md transition-colors cursor-pointer ${
                  statusFilter === s
                    ? s === "error"
                      ? "bg-red-500 text-white font-semibold"
                      : s === "ok"
                        ? "bg-emerald-500 text-white font-semibold"
                        : "bg-primary-500 text-dark-bg font-semibold"
                    : isDarkMode
                      ? "text-gray-400 hover:text-gray-200"
                      : "text-gray-500 hover:text-gray-700"
                }`}
              >
                {t(`integradores.status.${s}`)}
              </button>
            ))}
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 flex flex-col lg:grid lg:grid-cols-[1fr_460px] overflow-hidden">
          {/* List */}
          <div
            className={`flex flex-col overflow-hidden lg:border-r ${borderColor}`}
          >
            <div className="flex-1 overflow-auto">
              {evidences.length === 0 ? (
                deviceEvidences.length > 0 || hasReceivedRef.current ? (
                  <div className="flex flex-col items-center justify-center h-full py-16 px-6 text-center">
                    <i className={`ri-filter-off-line text-3xl ${textSecondary} mb-3`}></i>
                    <p className={`text-sm ${textColor}`}>
                      {t("integradores.empty", "Sin evidencias todavía")}
                    </p>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center h-full py-16 px-6 text-center">
                    <i
                      className={`ri-loader-4-line animate-spin text-3xl text-primary-500 mb-3`}
                    ></i>
                    <p className={`text-sm ${textColor}`}>
                      {t(
                        "integradores.waitingEvidence",
                        "Esperando evidencias de esta unidad…",
                      )}
                    </p>
                    <p className={`text-xs ${textSecondary} mt-1`}>
                      {t(
                        "integradores.waitingEvidenceHint",
                        "Las llamadas a los integradores aparecerán aquí en tiempo real.",
                      )}
                    </p>
                  </div>
                )
              ) : (
                evidences.map((ev) => (
                  <div
                    key={ev._localId}
                    onClick={() => setSelected(ev)}
                    className={`px-3 py-2 cursor-pointer border-b border-l-[3px] flex items-center gap-3 ${
                      selected?._localId === ev._localId
                        ? isDarkMode
                          ? "bg-dark-card border-b-dark-border border-l-primary-500"
                          : "bg-primary-50 border-b-gray-100 border-l-primary-500"
                        : isDarkMode
                          ? "bg-dark-surface border-b-dark-border border-l-transparent hover:bg-dark-card"
                          : "bg-white border-b-gray-100 border-l-transparent hover:bg-gray-50"
                    }`}
                  >
                    <span
                      className={`w-2 h-2 rounded-full shrink-0 ${ev.integrator_ok ? "bg-emerald-500" : "bg-red-500"}`}
                    />
                    <span
                      className={`text-[13px] font-semibold w-32 truncate ${isDarkMode ? "text-gray-200" : "text-gray-800"}`}
                    >
                      {ev.integrator_adapter}
                    </span>
                    {ev.ws_name && (
                      <span
                        className={`text-[10px] font-mono px-1.5 py-0.5 rounded shrink-0 truncate max-w-[150px] ${
                          isDarkMode
                            ? "bg-primary-500/10 text-primary-400"
                            : "bg-primary-50 text-primary-600"
                        }`}
                        title={ev.ws_name}
                      >
                        {ev.ws_name}
                      </span>
                    )}
                    {ev.historical && (
                      <span
                        className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-400 shrink-0 flex items-center gap-1"
                        title={t("integradores.historical.badge")}
                      >
                        <i className="ri-history-line"></i>
                        {t("integradores.historical.tag")}
                      </span>
                    )}
                    <span
                      className={`text-xs font-mono px-1.5 py-0.5 rounded ${
                        ev.http_status >= 200 && ev.http_status < 300
                          ? "bg-emerald-500/10 text-emerald-400"
                          : "bg-red-500/10 text-red-400"
                      }`}
                    >
                      {ev.http_status}
                    </span>
                    <span className={`text-[11px] ${textSecondary}`}>
                      {ev.latency_ms}ms
                    </span>
                    <span
                      className={`text-[11px] flex-1 truncate ${textSecondary}`}
                      title={ev.error || ev.job_id}
                    >
                      {ev.error || ev.job_id}
                    </span>
                    <span className={`text-[11px] shrink-0 ${textSecondary}`}>
                      {formatTime(ev.sent_at)}
                    </span>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Detail */}
          <div className={`flex flex-col overflow-hidden min-w-0 ${surfaceBg}`}>
            {!selected ? (
              <div className={`p-4 text-sm ${textSecondary}`}>
                {t("integradores.selectEvidence", "Selecciona una evidencia")}
              </div>
            ) : (
              <div className="flex flex-col overflow-auto p-3 gap-3">
                <div className="flex items-center justify-between gap-2">
                  <span
                    className={`text-base font-extrabold tracking-tight truncate ${isDarkMode ? "text-white" : "text-gray-900"}`}
                  >
                    {selected.integrator_adapter}
                  </span>
                  <span
                    className={`text-xs font-bold px-2.5 py-1 rounded-full shrink-0 ${
                      selected.integrator_ok
                        ? "bg-emerald-500/15 text-emerald-400"
                        : "bg-red-500/15 text-red-400"
                    }`}
                  >
                    {selected.integrator_ok ? "✓" : "✗"} {selected.http_status} ·{" "}
                    {selected.latency_ms}ms
                  </span>
                </div>

                {selected.historical && (
                  <div className="flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2">
                    <i className="ri-history-line text-amber-400 text-sm mt-0.5 shrink-0"></i>
                    <div className="flex flex-col min-w-0">
                      <span className="text-[11px] font-semibold text-amber-400">
                        {t("integradores.historical.badge")}
                      </span>
                      <span className={`text-[11px] ${textSecondary}`}>
                        {t("integradores.historical.description")}
                      </span>
                      <span
                        className={`text-[11px] ${textSecondary}`}
                      >
                        {t("integradores.historical.lastPosition", {
                          time: formatTime(selected.sent_at),
                        })}
                      </span>
                    </div>
                  </div>
                )}

                <div
                  className={`grid grid-cols-2 gap-x-4 gap-y-2 rounded-lg p-3 ${
                    isDarkMode
                      ? "bg-dark-bg border border-dark-border"
                      : "bg-white border border-gray-200"
                  }`}
                >
                  <Meta label={t("integradores.meta.imei")} value={selected.imei || "—"} isDarkMode={isDarkMode} />
                  <Meta label={t("integradores.meta.job")} value={selected.job_id || "—"} isDarkMode={isDarkMode} />
                  <Meta label={t("integradores.meta.device")} value={selected.device_id || "—"} isDarkMode={isDarkMode} />
                  <Meta label={t("integradores.meta.integratorStatus")} value={selected.integrator_ok ? t("integradores.integratorStatus.active") : t("integradores.integratorStatus.inactive")} isDarkMode={isDarkMode} />
                  <Meta label={t("integradores.meta.wsName")} value={selected.ws_name || "—"} isDarkMode={isDarkMode} />
                  <Meta label={t("integradores.meta.retransmission")} value={selected.retransmission_id ? retransmissionNameById.get(selected.retransmission_id) || selected.ws_name || selected.retransmission_id : "—"} isDarkMode={isDarkMode} />
                  <Meta label={t("integradores.meta.sent")} value={formatTime(selected.sent_at)} isDarkMode={isDarkMode} />
                  <Meta label={t("integradores.meta.resp")} value={formatTime(selected.response_at)} isDarkMode={isDarkMode} />
                </div>

                {selected.error && (
                  <div className="text-[11px] text-red-400 break-words border border-red-500/30 bg-red-500/10 rounded p-2">
                    {selected.error}
                  </div>
                )}

                {(["request", "response"] as const).map((field) => {
                  const formatted = formatPayload(selected[field]);
                  const { ext } = payloadFileMeta(selected[field]);
                  return (
                    <div key={field} className="flex flex-col">
                      <div className="flex items-center justify-between mb-1.5 gap-1 flex-wrap">
                        <span
                          className={`flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider ${isDarkMode ? "text-gray-200" : "text-gray-700"}`}
                        >
                          <span className="w-1.5 h-1.5 rounded-full bg-primary-500" />
                          {t(`integradores.${field}`, field)}
                        </span>
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => copyText(formatted, field)}
                            className={`${btnGhost} ${copied === field ? "!text-primary-500" : ""}`}
                          >
                            <i
                              className={copied === field ? "ri-check-line" : "ri-file-copy-line"}
                            ></i>
                            {copied === field
                              ? t("integradores.copied", "Copiado")
                              : t("integradores.copy", "Copiar")}
                          </button>
                          <button
                            onClick={() => handleDownloadText(field)}
                            className={btnGhost}
                          >
                            <i className="ri-download-line"></i>
                            {ext.toUpperCase()}
                          </button>
                          <button
                            onClick={() => handleScreenshot(field)}
                            disabled={busyShot === `${field}-png`}
                            className={btnGhost}
                          >
                            <i className="ri-image-line"></i>
                            PNG
                          </button>
                        </div>
                      </div>
                      <pre
                        className={`overflow-auto border rounded p-2.5 text-[11px] font-mono whitespace-pre-wrap break-words leading-relaxed max-h-72 ${
                          isDarkMode
                            ? "bg-dark-bg border-dark-border text-gray-300"
                            : "bg-white border-gray-200 text-gray-700"
                        }`}
                      >
                        {formatted}
                      </pre>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function Meta({
  label,
  value,
  isDarkMode,
}: {
  label: string;
  value: string;
  isDarkMode: boolean;
}) {
  return (
    <div className="flex items-baseline gap-1.5 min-w-0">
      <span
        className={`text-[10px] font-bold uppercase tracking-wide shrink-0 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
      >
        {label}
      </span>
      <span
        className={`text-[11px] font-mono truncate ${isDarkMode ? "text-gray-200" : "text-gray-800"}`}
        title={value}
      >
        {value}
      </span>
    </div>
  );
}
